import React from 'react';
import type { AiPersona, PersonaEmotion } from '@roll1/shared';

/** Tia metadata — duplicated from server (ErrorBoundary cannot import server modules) */
const TIA_COLOR = '#8B5CF6';
const TIA_INITIAL = 'Т';
const TIA_DISPLAY_NAME = 'Тиа';
const TIA_AVATAR_URL = '/assets/tia-avatar.png';

/** Emotion-specific art assets for Tia (200x200 WebP) */
export const TIA_EMOTION_URLS: Record<PersonaEmotion, string> = {
  neutral:    '/assets/tia/neutral.webp',
  interested: '/assets/tia/interested.webp',
  amused:     '/assets/tia/amused.webp',
  impressed:  '/assets/tia/impressed.webp',
  concerned:  '/assets/tia/concerned.webp',
  nostalgic:  '/assets/tia/nostalgic.webp',
  bored:      '/assets/tia/bored.webp',
};

interface PersonaAvatarProps {
  persona: AiPersona | null | undefined;
  size?: 'sm' | 'md' | 'lg';
  className?: string;
  emotion?: PersonaEmotion;
}

const SIZE_MAP = {
  sm: { wh: 'w-6 h-6', text: 'text-pill' },
  md: { wh: 'w-8 h-8', text: 'text-ui' },
  lg: { wh: 'w-12 h-12', text: 'text-xl' },
} as const;

/** Generate a deterministic color from a string (hash → hue) */
function hashColor(str: string): string {
  let hash = 0;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash + str.charCodeAt(i)) | 0;
  }
  const hue = ((hash % 360) + 360) % 360;
  return `hsl(${hue}, 60%, 55%)`;
}

/** Fallback level for Tia avatar: 0=emotion, 1=neutral, 2=tia-avatar.png, 3=initial */
type TiaFallbackLevel = 0 | 1 | 2 | 3;

function isTiaPersona(persona: AiPersona | null | undefined): boolean {
  if (!persona) return true;
  return persona.type === 'builtin' && persona.sourceId === 'tia';
}

function getPersonaVisuals(persona: AiPersona | null | undefined): {
  color: string;
  initial: string;
  displayName: string;
  avatarUrl?: string;
} | null {
  // null = no persona → don't render
  if (persona === null) return null;

  // undefined or builtin:tia = default Tia
  if (!persona || isTiaPersona(persona)) {
    return { color: TIA_COLOR, initial: TIA_INITIAL, displayName: TIA_DISPLAY_NAME, avatarUrl: TIA_AVATAR_URL };
  }

  // journal/sheet — hash color from sourceId
  const initial = persona.displayName ? persona.displayName[0].toUpperCase() : '?';
  return {
    color: hashColor(persona.sourceId),
    initial,
    displayName: persona.displayName,
  };
}

const PersonaAvatar = React.memo(function PersonaAvatar({
  persona,
  size = 'md',
  className = '',
  emotion,
}: PersonaAvatarProps) {
  const visuals = getPersonaVisuals(persona);
  const [fallbackLevel, setFallbackLevel] = React.useState<TiaFallbackLevel>(0);

  // Reset fallback when emotion changes
  React.useEffect(() => {
    setFallbackLevel(0);
  }, [emotion]);

  if (!visuals) return null;

  const s = SIZE_MAP[size];
  const isTia = isTiaPersona(persona);

  // Determine avatar URL based on emotion + fallback chain
  let avatarUrl: string | undefined;
  if (isTia) {
    if (emotion && fallbackLevel === 0) {
      avatarUrl = TIA_EMOTION_URLS[emotion];
    } else if (fallbackLevel <= 1 && emotion) {
      // Fallback to neutral emotion asset
      avatarUrl = TIA_EMOTION_URLS.neutral;
    } else if (fallbackLevel <= 2) {
      // Fallback to static tia-avatar.png
      avatarUrl = TIA_AVATAR_URL;
    }
    // fallbackLevel 3 = initial+color (no avatarUrl)
  } else {
    avatarUrl = visuals.avatarUrl;
  }

  if (avatarUrl) {
    return (
      <img
        src={avatarUrl}
        alt={visuals.displayName}
        className={`${s.wh} rounded-full object-cover ${className}`}
        onError={() => {
          if (isTia) {
            setFallbackLevel(prev => Math.min(prev + 1, 3) as TiaFallbackLevel);
          }
        }}
      />
    );
  }

  return (
    <div
      className={`${s.wh} ${s.text} flex items-center justify-center rounded-full font-semibold text-fg ${className}`}
      style={{ backgroundColor: visuals.color }}
      role="img"
      aria-label={visuals.displayName}
    >
      {visuals.initial}
    </div>
  );
});

export { TIA_COLOR, TIA_INITIAL, TIA_DISPLAY_NAME, getPersonaVisuals };
export type { PersonaAvatarProps };
export default PersonaAvatar;
